import { useQuery } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import type { DayHours, StoreHoursPublic } from "@passa-a-pagina/shared";
import { api } from "./api";

/** Public opening hours (structured week + a server-rendered PT string). */
export function useStoreHours() {
  return useQuery<StoreHoursPublic>({
    queryKey: ["store-hours"],
    queryFn: () => api<StoreHoursPublic>("/api/store-hours"),
    staleTime: 60 * 60 * 1000,
    retry: false,
  });
}

const KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;

function fmtTime(hhmm: string): string {
  const [h, m] = hhmm.split(":").map((n) => parseInt(n, 10));
  return m ? `${h}h${String(m).padStart(2, "0")}` : `${h}h`;
}

/**
 * Renders a localised opening-hours string from the Monday-first days array,
 * e.g. "Mon–Thu & Sat: 9h–17h" (EN) / "Seg–Qui e Sáb: 9h–17h" (PT). Mirrors the
 * server's renderHoursDisplay but uses i18n day abbreviations so the public
 * site shows hours in the active language.
 */
export function formatStoreHours(days: DayHours[], t: TFunction): string {
  const abbr = (i: number) => t(`hours.short.${KEYS[i]}`);
  const runs: { start: number; end: number; key: string; open: string; close: string }[] = [];
  days.forEach((d, i) => {
    if (!d) return;
    const key = `${d.open}-${d.close}`;
    const last = runs[runs.length - 1];
    if (last && last.key === key && last.end === i - 1) last.end = i;
    else runs.push({ start: i, end: i, key, open: d.open, close: d.close });
  });
  if (runs.length === 0) return t("hours.closed");

  const labelFor = (r: { start: number; end: number }) =>
    r.start === r.end ? abbr(r.start) : `${abbr(r.start)}–${abbr(r.end)}`;

  const byWindow = new Map<string, { labels: string[]; open: string; close: string }>();
  for (const r of runs) {
    const g = byWindow.get(r.key);
    if (g) g.labels.push(labelFor(r));
    else byWindow.set(r.key, { labels: [labelFor(r)], open: r.open, close: r.close });
  }

  return [...byWindow.values()]
    .map((g) => {
      const labels =
        g.labels.length > 1
          ? `${g.labels.slice(0, -1).join(", ")} ${t("hours.and")} ${g.labels[g.labels.length - 1]!}`
          : g.labels[0]!;
      return `${labels}: ${fmtTime(g.open)}–${fmtTime(g.close)}`;
    })
    .join("; ");
}
